import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CardLimits } from "../../../services/cards/mocks";
import { Block, Skeleton } from "../../shared";
import { formatNumber } from "../../../utils";

interface CardLimitsBlockProps {
  limits?: CardLimits;
  isLoading?: boolean;
}

export function CardLimitsBlock({ limits, isLoading }: CardLimitsBlockProps) {
  const { t } = useTranslation("cards");
  if (isLoading) {
    return (
      <Block sx={{ padding: "16px 18px" }}>
        <Skeleton
          variant="rounded"
          width={100}
          height={22}
          sx={{ borderRadius: "8px", marginBottom: "12px" }}
        />
        <Skeleton
          variant="rounded"
          height={12}
          sx={{ borderRadius: "6px", marginBottom: "10px" }}
        />
        <Skeleton
          variant="rounded"
          width={180}
          height={16}
          sx={{ borderRadius: "6px" }}
        />
      </Block>
    );
  }

  if (!limits) {
    return null;
  }

  const percent = Math.min(
    Math.round((limits.used / limits.monthly) * 100),
    100
  );
  const remaining = Math.max(limits.monthly - limits.used, 0);

  return (
    <Block sx={{ padding: "16px 18px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <Typography sx={{ fontSize: "17px", fontWeight: 600 }}>
          {t("limits")}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: "rgba(255, 255, 255, 0.5)",
          }}
        >
          {t("limits_reset")}
        </Typography>
      </Box>

      <Box
        sx={{
          width: "100%",
          height: "8px",
          borderRadius: "999px",
          bgcolor: "rgba(255, 255, 255, 0.06)",
          overflow: "hidden",
          marginBottom: "10px",
        }}
      >
        <Box
          sx={{
            width: `${percent}%`,
            height: "100%",
            borderRadius: "999px",
            background:
              "linear-gradient(90deg, #3377FF 0%, #6DA1FF 100%)",
            transition: "width 0.3s",
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "11px",
              color: "rgba(255, 255, 255, 0.5)",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            {t("spent_this_month")}
          </Typography>
          <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
            ${formatNumber(String(limits.used))}
            <Box
              component="span"
              sx={{
                fontSize: "13px",
                color: "rgba(255, 255, 255, 0.4)",
                marginLeft: "6px",
                fontWeight: 400,
              }}
            >
              {t("of_amount", { amount: `$${formatNumber(String(limits.monthly))}` })}
            </Box>
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography
            sx={{
              fontSize: "11px",
              color: "rgba(255, 255, 255, 0.5)",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            {t("left")}
          </Typography>
          <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
            ${formatNumber(String(remaining))}
          </Typography>
        </Box>
      </Box>
    </Block>
  );
}
